/**
 * The Wordle-style share text copied to the clipboard on a win.
 *
 * The emoji grid renders the solved PATH, not just the board's outline.
 * That distinction matters: plenty of levels have zero holes (the "open"
 * shape family — see docs/ARCHITECTURE.md), and a grid of identical blue squares would
 * be a boring, unshareable image. Every level has a unique solution, and a
 * completed game's path always equals it, so the grid can be built straight
 * from `level.solution` with no live game state — each cell shows the
 * direction the solution moves out of it, which makes every day's grid a
 * distinct little snake regardless of how many holes that day's board has.
 */

import type { Level, NodeId } from "../scripts/lib/types.ts";

const HOLE = "⬛";
const START = "🟢";
const EXIT = "🏁";
const ARROW_UP = "⬆️";
const ARROW_DOWN = "⬇️";
const ARROW_LEFT = "⬅️";
const ARROW_RIGHT = "➡️";

export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function arrowInto(fromId: NodeId, toId: NodeId, cols: number): string {
  const fromRow = Math.floor(fromId / cols);
  const fromCol = fromId % cols;
  const toRow = Math.floor(toId / cols);
  const toCol = toId % cols;
  if (fromRow === toRow) return toCol > fromCol ? ARROW_RIGHT : ARROW_LEFT;
  return toRow > fromRow ? ARROW_DOWN : ARROW_UP;
}

function buildPathGrid(level: Level): string {
  const { rows, cols, holes, solution, start, exit } = level;
  const holeSet = new Set(holes);
  const outArrow = new Map<NodeId, string>();
  for (let i = 0; i < solution.length - 1; i++) {
    outArrow.set(solution[i], arrowInto(solution[i], solution[i + 1], cols));
  }

  const lines: string[] = [];
  for (let r = 0; r < rows; r++) {
    let line = "";
    for (let c = 0; c < cols; c++) {
      const id = r * cols + c;
      if (holeSet.has(id)) line += HOLE;
      else if (id === start) line += START;
      else if (id === exit) line += EXIT;
      else line += outArrow.get(id) ?? HOLE;
    }
    lines.push(line);
  }
  return lines.join("\n");
}

export function buildShareText(day: number, elapsedMs: number, level: Level, url: string): string {
  const grid = buildPathGrid(level);
  return `Path Puzzle 🧩 Day ${day}\n⏱ ${formatElapsed(elapsedMs)}\n\n${grid}\n\n${url}`;
}
